import { useState, useEffect } from 'react';
import { Shield, AlertTriangle, FileText, Trash2, Users, RefreshCw, X, CheckCircle2 } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';
import { useToast } from '../ui';

type Tab = 'incidents' | 'audit' | 'cleanup' | 'transfers';

interface Incident {
  id: string;
  reported_at: string;
  reporter_name: string | null;
  category: string;
  severity: string;
  title: string;
  status: string;
  source: string;
  parties_notified_at: string | null;
  sla_breach_logged: boolean;
}

interface Member {
  id: string;
  organization_name: string;
  status: string;
  deactivated_at: string | null;
}

interface StudyAssociation {
  id: string;
  name: string;
  status: string;
}

interface TransferRequest {
  id: string;
  requested_at: string;
  status: string;
  member_name: string;
  from_association_name: string;
  to_association_name: string;
  reason: string | null;
}

export default function DpaCompliance() {
  const [tab, setTab] = useState<Tab>('incidents');

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-kf-primary flex items-center gap-2">
          <Shield size={22} /> DPA-compliance
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Sikkerhetsbrudd (§10), revisjonsrapporter (§11), GDPR-cleanup (§12) og medlem-overgang/uttreden (Fase 4).
        </p>
      </div>

      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {[
          { k: 'incidents' as Tab, l: 'Sikkerhetsbrudd', icon: <AlertTriangle size={14} /> },
          { k: 'audit' as Tab, l: 'Audit-rapporter', icon: <FileText size={14} /> },
          { k: 'cleanup' as Tab, l: 'GDPR-cleanup', icon: <Trash2 size={14} /> },
          { k: 'transfers' as Tab, l: 'Overgang/uttreden', icon: <Users size={14} /> }
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-3 py-2 flex items-center gap-1.5 text-sm font-medium border-b-2 transition ${
              tab === t.k
                ? 'border-kf-primary text-kf-primary'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t.icon} {t.l}
          </button>
        ))}
      </div>

      {tab === 'incidents' && <IncidentsPanel />}
      {tab === 'audit' && <AuditReportPanel />}
      {tab === 'cleanup' && <GdprCleanupPanel />}
      {tab === 'transfers' && <TransfersPanel />}
    </div>
  );
}

// ================ INCIDENTS ================
function IncidentsPanel() {
  const toast = useToast();
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await authenticatedFetch('/api/admin/security-incidents?status=all');
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Feil');
      setIncidents(d.incidents || []);
    } catch (e: any) {
      toast.showError(e.message);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <h3 className="font-medium text-gray-900">Sikkerhetsbrudd (§10)</h3>
        <div className="flex gap-2">
          <button onClick={load} className="px-2 py-1 text-xs border border-gray-300 rounded flex items-center gap-1">
            <RefreshCw size={12} /> Oppdater
          </button>
          <button onClick={() => setShowNew(true)} className="px-3 py-1 text-xs bg-kf-primary text-white rounded">
            + Registrer nytt
          </button>
        </div>
      </div>

      {loading && <p className="text-sm text-gray-500">Laster…</p>}
      {!loading && incidents.length === 0 && (
        <div className="rounded border border-green-200 bg-green-50 p-4 text-sm text-green-800 flex items-center gap-2">
          <CheckCircle2 size={16} /> Ingen sikkerhetsbrudd registrert
        </div>
      )}
      {!loading && incidents.length > 0 && (
        <div className="border border-gray-200 rounded overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">Dato</th>
                <th className="px-3 py-2 text-left">Alvorlighet</th>
                <th className="px-3 py-2 text-left">Tittel</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Kilde</th>
                <th className="px-3 py-2 text-left">24t-SLA</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {incidents.map(i => (
                <tr key={i.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-700 text-xs">
                    {new Date(i.reported_at).toLocaleString('nb-NO', { timeZone: 'Europe/Oslo' })}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 text-xs rounded ${
                      i.severity === 'critical' ? 'bg-red-100 text-red-800' :
                      i.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                      i.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>{i.severity}</span>
                  </td>
                  <td className="px-3 py-2 text-gray-900">{i.title}</td>
                  <td className="px-3 py-2 text-xs">{i.status}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{i.source === 'system_error' ? '⚙️ auto' : '👤 manuell'}</td>
                  <td className="px-3 py-2 text-xs">
                    {i.parties_notified_at
                      ? <span className="text-green-700">✓ varslet</span>
                      : i.sla_breach_logged
                        ? <span className="text-red-700">SLA-brudd</span>
                        : <span className="text-amber-700">ikke varslet</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && <NewIncidentModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewIncidentModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [form, setForm] = useState({ category: 'data_leak', severity: 'medium', title: '', description: '', mitigation: '' });
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!form.title || !form.description) {
      toast.showError('Tittel og beskrivelse påkrevd');
      return;
    }
    setSaving(true);
    try {
      const r = await authenticatedFetch('/api/admin/security-incidents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Feil');
      toast.showSuccess('Sikkerhetsbrudd registrert');
      onSaved();
    } catch (e: any) {
      toast.showError(e.message);
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5">
        <div className="flex justify-between items-start mb-3">
          <h3 className="font-semibold text-gray-900">Registrer nytt sikkerhetsbrudd</h3>
          <button onClick={onClose}><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-600">Kategori</label>
            <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm">
              <option value="data_leak">Datalekkasje</option>
              <option value="unauthorized_access">Uautorisert tilgang</option>
              <option value="system_failure">Systemsvikt</option>
              <option value="integrity_loss">Integritetstap</option>
              <option value="other">Annet</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-600">Alvorlighet</label>
            <select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm">
              <option value="low">Lav</option>
              <option value="medium">Medium</option>
              <option value="high">Høy</option>
              <option value="critical">Kritisk</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-600">Tittel</label>
            <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                   className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
          </div>
          <div>
            <label className="text-xs text-gray-600">Beskrivelse</label>
            <textarea rows={4} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
          </div>
          <div>
            <label className="text-xs text-gray-600">Mitigering (hva er gjort)</label>
            <textarea rows={2} value={form.mitigation} onChange={e => setForm(f => ({ ...f, mitigation: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 border border-gray-300 rounded text-sm">Avbryt</button>
          <button onClick={submit} disabled={saving} className="px-3 py-1.5 bg-kf-primary text-white rounded text-sm disabled:opacity-50">
            {saving ? 'Lagrer…' : 'Registrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ================ AUDIT REPORT ================
function AuditReportPanel() {
  const toast = useToast();
  const [members, setMembers] = useState<Member[]>([]);
  const [associations, setAssociations] = useState<StudyAssociation[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const r1 = await authenticatedFetch('/api/admin/members');
        const d1 = await r1.json();
        setMembers(d1.members || []);
        const r2 = await authenticatedFetch('/api/admin/study-associations');
        const d2 = await r2.json();
        setAssociations(d2.study_associations || []);
      } catch (e: any) { toast.showError(e.message); }
    })();
  }, []);

  async function generatePdf(partyType: 'medlem' | 'studieforbund', partyId: string, name: string) {
    try {
      const r = await authenticatedFetch(`/api/admin/audit-report/${partyType}/${partyId}`);
      if (!r.ok) { const d = await r.json(); throw new Error(d.message || 'Feil'); }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `revisjonsrapport-${partyType}-${name.replace(/\s+/g, '_')}-${new Date().toISOString().slice(0,10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.showSuccess('Rapport generert og lastet ned');
    } catch (e: any) { toast.showError(e.message); }
  }

  return (
    <div className="space-y-4">
      <div className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        Revisjonsrapport (§11) genereres som PDF og dekker siste 12 mnd. Innehold:
        volum-statistikk, underdatabehandlere, sikkerhetshendelser, audit-log-sammendrag,
        full Vedlegg A-tekst.
      </div>

      <div>
        <h4 className="font-medium text-sm text-gray-800 mb-2">Studieforbund</h4>
        <ul className="divide-y divide-gray-200 border border-gray-200 rounded">
          {associations.map(a => (
            <li key={a.id} className="px-3 py-2 flex justify-between items-center">
              <span className="text-sm">{a.name} <span className="text-xs text-gray-500">({a.status})</span></span>
              <button onClick={() => generatePdf('studieforbund', a.id, a.name)}
                      className="px-3 py-1 text-xs bg-kf-primary text-white rounded">
                Generer rapport
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="font-medium text-sm text-gray-800 mb-2">Medlemsorganisasjoner</h4>
        <ul className="divide-y divide-gray-200 border border-gray-200 rounded max-h-96 overflow-y-auto">
          {members.map(m => (
            <li key={m.id} className="px-3 py-2 flex justify-between items-center">
              <span className="text-sm">{m.organization_name} <span className="text-xs text-gray-500">({m.status})</span></span>
              <button onClick={() => generatePdf('medlem', m.id, m.organization_name)}
                      className="px-3 py-1 text-xs bg-kf-primary text-white rounded">
                Generer rapport
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ================ GDPR CLEANUP ================
function GdprCleanupPanel() {
  const toast = useToast();
  const [members, setMembers] = useState<Member[]>([]);
  const [preview, setPreview] = useState<any>(null);
  const [previewFor, setPreviewFor] = useState<string | null>(null);
  const [executing, setExecuting] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await authenticatedFetch('/api/admin/members');
      const d = await r.json();
      setMembers((d.members || []).filter((m: Member) => m.status === 'inactive'));
    })();
  }, []);

  async function runPreview(memberId: string, name: string) {
    try {
      setPreviewFor(name);
      setPreview(null);
      const r = await authenticatedFetch(`/api/admin/gdpr-cleanup/${memberId}/preview`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Feil');
      setPreview(d);
    } catch (e: any) { toast.showError(e.message); setPreviewFor(null); }
  }

  async function execute(memberId: string) {
    if (!confirm('Dette VIL slette persondata. Bekreft med JA i neste prompt?')) return;
    const typed = prompt('Skriv "YES_DELETE_PERSONAL_DATA" (eksakt) for å bekrefte:');
    if (typed !== 'YES_DELETE_PERSONAL_DATA') { toast.showError('Avbrutt — bekreftelsestekst stemte ikke'); return; }
    setExecuting(true);
    try {
      const r = await authenticatedFetch(`/api/admin/gdpr-cleanup/${memberId}/execute`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmation: 'YES_DELETE_PERSONAL_DATA' })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Feil');
      toast.showSuccess('GDPR-cleanup fullført');
      setPreview(null); setPreviewFor(null);
    } catch (e: any) { toast.showError(e.message); } finally { setExecuting(false); }
  }

  return (
    <div className="space-y-4">
      <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <strong>§12 GDPR-cleanup.</strong> Attester + lommebok-innhold bevares.
        Course_candidates med bevart session pseudonymiseres (navn → "[Arkivert]").
        Resten (sessions uten attest, tomme instanser) slettes individuelt.
        Alltid preview først — eksplisitt bekreftelse ved sletting.
      </div>

      {members.length === 0 && <p className="text-sm text-gray-500">Ingen arkiverte medlemmer å rydde (status=inactive).</p>}

      {members.length > 0 && (
        <ul className="divide-y divide-gray-200 border border-gray-200 rounded">
          {members.map(m => (
            <li key={m.id} className="px-3 py-2 flex justify-between items-center">
              <div>
                <span className="text-sm font-medium">{m.organization_name}</span>
                <div className="text-xs text-gray-500">
                  Arkivert: {m.deactivated_at ? new Date(m.deactivated_at).toLocaleDateString('nb-NO') : 'ikke satt'}
                </div>
              </div>
              <button onClick={() => runPreview(m.id, m.organization_name)}
                      className="px-3 py-1 text-xs bg-kf-primary text-white rounded">
                Preview cleanup
              </button>
            </li>
          ))}
        </ul>
      )}

      {preview && previewFor && (
        <div className="rounded border border-gray-300 bg-white p-4">
          <h4 className="font-medium text-sm mb-2">Preview — {previewFor}</h4>
          <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto border border-gray-200">
            {JSON.stringify(preview.summary, null, 2)}
          </pre>
          <div className="mt-3 flex gap-2">
            <button onClick={() => execute(preview.member.id)} disabled={executing}
                    className="px-3 py-1.5 bg-red-600 text-white rounded text-sm disabled:opacity-50">
              {executing ? 'Kjører…' : 'Utfør cleanup'}
            </button>
            <button onClick={() => { setPreview(null); setPreviewFor(null); }}
                    className="px-3 py-1.5 border border-gray-300 rounded text-sm">Avbryt</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ================ TRANSFERS ================
function TransfersPanel() {
  const toast = useToast();
  const [requests, setRequests] = useState<TransferRequest[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [associations, setAssociations] = useState<StudyAssociation[]>([]);

  async function load() {
    const [r1, r2, r3] = await Promise.all([
      authenticatedFetch('/api/admin/member-transfer-requests'),
      authenticatedFetch('/api/admin/members'),
      authenticatedFetch('/api/admin/study-associations')
    ]);
    const [d1, d2, d3] = await Promise.all([r1.json(), r2.json(), r3.json()]);
    setRequests(d1.requests || []);
    setMembers(d2.members || []);
    setAssociations(d3.study_associations || []);
  }

  useEffect(() => { load(); }, []);

  async function enableTransfer(memberId: string, name: string) {
    const reason = prompt(`Begrunnelse for å åpne 30d overgangsvindu for ${name}:`);
    if (!reason || reason.length < 10) { toast.showError('Begrunnelse påkrevd (min 10 tegn)'); return; }
    try {
      const r = await authenticatedFetch(`/api/admin/members/${memberId}/enable-transfer-search`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ duration_days: 30, reason })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Feil');
      toast.showSuccess(`Overgangsvindu åpnet for ${name} (30 dager)`);
    } catch (e: any) { toast.showError(e.message); }
  }

  async function deactivateAssociation(assocId: string, name: string) {
    const source = prompt(`Uttredens-type for ${name} — skriv "voluntary" eller "board_decision":`);
    if (!['voluntary', 'board_decision'].includes(source || '')) { toast.showError('Ugyldig verdi'); return; }
    const reason = prompt('Begrunnelse (min 20 tegn):');
    if (!reason || reason.length < 20) { toast.showError('Begrunnelse påkrevd (min 20 tegn)'); return; }
    if (!confirm(`Dette arkiverer ${name} og åpner 90d overgangsvindu for alle medlemmer. Fortsette?`)) return;
    try {
      const r = await authenticatedFetch(`/api/admin/study-associations/${assocId}/initiate-deactivation`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source, reason })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Feil');
      toast.showSuccess(`${name} arkivert. ${d.members_opened_for_transfer} medlemmer åpnet for overgang.`);
      load();
    } catch (e: any) { toast.showError(e.message); }
  }

  return (
    <div className="space-y-5">
      <div className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        Medlem-overgang er gated — åpnes per medlem (30d) etter e-postsøknad, eller
        automatisk 90d for alle medlemmer når studieforbund arkiveres.
      </div>

      <div>
        <h4 className="font-medium text-sm text-gray-800 mb-2">Pending overgangssøknader</h4>
        {requests.length === 0 ? (
          <p className="text-xs text-gray-500">Ingen pending søknader.</p>
        ) : (
          <ul className="divide-y divide-gray-200 border border-gray-200 rounded">
            {requests.map(r => (
              <li key={r.id} className="px-3 py-2 text-sm">
                <strong>{r.member_name}</strong>: {r.from_association_name} → {r.to_association_name}
                <div className="text-xs text-gray-500">
                  {new Date(r.requested_at).toLocaleString('nb-NO', { timeZone: 'Europe/Oslo' })} · status: {r.status}
                </div>
                {r.reason && <div className="text-xs text-gray-700 mt-1 italic">"{r.reason}"</div>}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h4 className="font-medium text-sm text-gray-800 mb-2">Åpne overgangsvindu per medlem (30d)</h4>
        <ul className="divide-y divide-gray-200 border border-gray-200 rounded max-h-80 overflow-y-auto">
          {members.filter(m => m.status === 'active').map(m => (
            <li key={m.id} className="px-3 py-2 flex justify-between items-center text-sm">
              <span>{m.organization_name}</span>
              <button onClick={() => enableTransfer(m.id, m.organization_name)}
                      className="px-3 py-1 text-xs bg-kf-primary text-white rounded">
                Åpne vindu
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="font-medium text-sm text-gray-800 mb-2">Arkiver studieforbund (uttreden)</h4>
        <ul className="divide-y divide-gray-200 border border-gray-200 rounded">
          {associations.filter(a => a.status === 'active').map(a => (
            <li key={a.id} className="px-3 py-2 flex justify-between items-center text-sm">
              <span>{a.name}</span>
              <button onClick={() => deactivateAssociation(a.id, a.name)}
                      className="px-3 py-1 text-xs bg-red-600 text-white rounded">
                Arkiver (uttreden)
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
